import React from 'react';

import { routes } from '@aline/routes';
import { CircularProgress, Typography } from '@mui/material';
import { Route, Routes, useLocation } from 'react-router-dom';
import { PanelHeader } from 'src/components';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import { AddInputDrawer } from 'src/editor/toolbars/right-panel/add-inputs';
import { PlaybooksDrawerContent } from 'src/editor/toolbars/right-panel/ai';
import { CommentsDrawer } from 'src/editor/toolbars/right-panel/comments';
import { DocumentSummaryDrawer } from 'src/editor/toolbars/right-panel/document-summary/DocumentSummaryDrawer';
import { DocumentVariables } from 'src/editor/toolbars/right-panel/document-variables';
import { CRMIntegrationDrawer } from 'src/editor/toolbars/right-panel/integrations/CRMIntegration';
import { IntegrationDrawer } from 'src/editor/toolbars/right-panel/integrations/IntegrationDrawer';
import { TrackedChangesDrawer } from 'src/editor/toolbars/right-panel/tracked-changes';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { DocumentPreviewContent } from './drawer-content/DocumentPreviewContent';
import { FocusedViewNotAvailable } from './drawer-content/FocusedViewNotAvailable';
import { ReminderDetailsContent } from './drawer-content/ReminderDetailsContent';
import { ReportAutomationsContent } from './drawer-content/ReportAutomationsContent';
import { VersionHistoryDrawerContent } from './drawer-content/VersionHistoryDrawerContent';
import { WorkflowSidePanelContent } from './drawer-content/WorkflowSidePanelContent';
import { useIsFocusedView } from './focused-view';
import type { DrawerRouteMatch, DrawerRouteType } from './GlobalRightDrawer';
import { getDrawerRouteMatchFromPathname } from './GlobalRightDrawer';

const useStyles = makeStyles()(theme => ({
  editorDrawerLoading: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
}));

const DRAWER_PATTERNS = [
  routes.ChatIndex,
  routes.ChatDetail,
  routes.WorkflowDetailTabTaskThread,
  routes.WorkflowDetailTabThread,
  routes.DocumentDetailComments,
  routes.DocumentDetailFields,
  routes.DocumentDetailAddInput,
  routes.DocumentDetailSummary,
  routes.DocumentDetailTrackedChanges,
  routes.DocumentDetailIntegrationDetail,
  routes.DocumentDetailIntegrations,
  routes.DocumentDetailPlaybooks,
  routes.DocumentDetailVersionDetail,
  routes.DocumentDetail,
  routes.RepositoryDocumentPreview,
  routes.PartyDetailDocumentPreview,
  routes.RemindersPreview,
  routes.ReportReportDetailAutomations,
];

/** Drawer sections an external user's focused view leaves out: AI and authoring tools. */
const FOCUSED_VIEW_HIDDEN_DRAWERS: ReadonlySet<DrawerRouteType> = new Set<DrawerRouteType>([
  'add-input',
  'ai',
  'default',
  'integrations',
  'playbooks',
  'summary',
]);

/**
 * Wraps children in a Routes context so useParams() works inside drawer components.
 * The GlobalRightDrawer renders outside the main Routes tree, so without this wrapper
 * useParams() returns {} and useDocumentParams() throws.
 */
export function DrawerRouteParamsProvider({ children }: { children: React.ReactNode }) {
  return (
    <Routes>
      {DRAWER_PATTERNS.map(pattern => (
        <Route
          key={pattern}
          element={children}
          path={pattern.endsWith('/*') ? pattern : `${pattern}/*`}
        />
      ))}
      <Route element={children} path="*" />
    </Routes>
  );
}

/**
 * Renders the appropriate drawer content based on the current route.
 */
export function GlobalRightDrawerContent(): JSX.Element {
  return (
    <DrawerRouteParamsProvider>
      <DrawerContentInner />
    </DrawerRouteParamsProvider>
  );
}

function DrawerContentInner(): JSX.Element {
  const location = useLocation();
  const isFocused = useIsFocusedView();
  const routeMatch = getDrawerRouteMatchFromPathname(location.pathname);
  if (isFocused && FOCUSED_VIEW_HIDDEN_DRAWERS.has(routeMatch.type))
    return <FocusedViewNotAvailable />;

  return <DrawerContentSwitch routeMatch={routeMatch} />;
}

interface DrawerContentSwitchProps {
  routeMatch: DrawerRouteMatch;
}

const INTEGRATION_VALUES = new Set<string>(Object.values(Apollo.Integration));

function getIsIntegration(value: string): value is Apollo.Integration {
  return INTEGRATION_VALUES.has(value);
}

function DrawerContentSwitch({ routeMatch }: DrawerContentSwitchProps): JSX.Element {
  switch (routeMatch.type) {
    case 'comments':
      return (
        <EditorRuntimeGate>
          <CommentsDrawer />
        </EditorRuntimeGate>
      );

    case 'fields':
      return (
        <EditorRuntimeGate>
          <DocumentVariables />
        </EditorRuntimeGate>
      );

    case 'add-input':
      return (
        <EditorRuntimeGate>
          <AddInputDrawer />
        </EditorRuntimeGate>
      );

    case 'summary':
      return (
        <EditorRuntimeGate>
          <DocumentSummaryDrawer documentId={routeMatch.params.documentId} />
        </EditorRuntimeGate>
      );

    case 'track-changes':
      return (
        <EditorRuntimeGate>
          <TrackedChangesDrawer />
        </EditorRuntimeGate>
      );

    case 'integrations': {
      const integration = routeMatch.params.integration;
      if (integration && getIsIntegration(integration)) {
        return (
          <EditorRuntimeGate>
            <CRMIntegrationDrawer integration={integration} />
          </EditorRuntimeGate>
        );
      }

      return (
        <EditorRuntimeGate>
          <IntegrationDrawer />
        </EditorRuntimeGate>
      );
    }

    case 'playbooks':
      return (
        <EditorRuntimeGate>
          <PlaybooksDrawerContent />
        </EditorRuntimeGate>
      );

    case 'document-preview':
      return <DocumentPreviewContent documentId={routeMatch.params.documentId} />;

    case 'reminder-details':
      return <ReminderDetailsContent reminderId={routeMatch.params.reminderId} />;

    case 'report-automations':
      return <ReportAutomationsContent reportId={routeMatch.params.reportId} />;

    case 'workflow-side-panel':
      return <WorkflowSidePanelContent />;

    case 'version-history':
      return <VersionHistoryDrawerContent />;

    case 'default':
    default:
      return <></>;
  }
}

function EditorRuntimeGate({ children }: { children: React.ReactNode }): JSX.Element {
  const runtime = useMaybeEditorRuntime();
  if (!runtime) return <EditorDrawerLoading />;
  return <>{children}</>;
}

function EditorDrawerLoading(): JSX.Element {
  const { classes } = useStyles();

  return (
    <>
      <PanelHeader showBackButton backButtonTooltip="Back to AI" text="Editor" />
      <div className={classes.editorDrawerLoading}>
        <CircularProgress size={28} />
        <Typography align="center">Loading editor...</Typography>
      </div>
    </>
  );
}
